/**
 * @unscoped — OnboardingWizard 의 i18n — Cycle 55-56.
 *
 * cycle 50 approval-i18n / cycle 51 calendar-i18n / cycle 52 workspace-i18n /
 * cycle 54 mail-i18n 통합 패턴 일관. SaaS 사용자 첫 진입점 (회사 가입 4-step
 * wizard) 의 i18n 정착 — branch tenant-saas-onboarding 의 핵심 page.
 *
 * 범위:
 *   - cycle 55: OnboardingWizard.tsx + steps/Step1-4 (5 components)
 *   - cycle 56: shared/DomainAvailabilityIndicator + RestoreBanner +
 *               VerificationMethodSelector + OnboardingExpiredPage +
 *               OnboardingRaceRejectedPage (5 components)
 *   → OnboardingWizard 도메인 0 violation 도달.
 *
 * 정책 (cycle 46/49 학습 적용):
 *   - fallback chain: lang → en → FALLBACK_LANG (ko)
 *   - 전수 non-empty 가드는 본 모듈 test 가 강제
 */

import {
  getSharedLang, setSharedLang, subscribeSharedLang,
  FALLBACK_LANG, type Lang,
} from './base-i18n';

const MESSAGES: Record<string, Record<Lang, string>> = {
  // ----- Wizard root (OnboardingWizard.tsx) -----
  'onboarding.wizard.title': { ko: '새 회사 가입', en: 'New company sign-up', ja: '新規企業登録' },
  'onboarding.wizard.subtitle': {
    ko: '회사 정보와 도메인을 등록하고 관리자 계정을 만드세요.',
    en: 'Register your company info and domain, then create your admin account.',
    ja: '会社情報とドメインを登録し、管理者アカウントを作成してください。',
  },
  'onboarding.wizard.nav_aria': { ko: '가입 단계', en: 'Sign-up steps', ja: '登録ステップ' },
  'onboarding.wizard.stepper_aria': {
    ko: '회사 가입 단계',
    en: 'Company sign-up steps',
    ja: '企業登録ステップ',
  },
  'onboarding.wizard.restoring': {
    ko: '세션을 복원하는 중...',
    en: 'Restoring session…',
    ja: 'セッションを復元中...',
  },

  // ----- 공통 step 라벨 (Stepper 표시 + heading 재사용) -----
  'onboarding.step.label.company': { ko: '회사 정보', en: 'Company info', ja: '会社情報' },
  'onboarding.step.label.domain': { ko: '도메인 등록', en: 'Domain', ja: 'ドメイン' },
  'onboarding.step.label.verify': { ko: '도메인 검증', en: 'Domain verification', ja: 'ドメイン検証' },
  'onboarding.step.label.admin': { ko: '관리자 계정', en: 'Admin account', ja: '管理者アカウント' },

  // ----- Step1 — 회사 정보 -----
  'onboarding.step1.subheading': {
    ko: '가입하실 회사의 이름을 입력해 주세요.',
    en: 'Enter the name of the company you are registering.',
    ja: '登録する会社の名前を入力してください。',
  },
  'onboarding.step1.label_name': { ko: '회사 이름', en: 'Company name', ja: '会社名' },
  'onboarding.step1.placeholder_name': { ko: '예: 타임소프트', en: 'e.g. Acme Corp', ja: '例: タイムソフト' },
  'onboarding.step1.hint': {
    ko: '한글 / 영문 / 숫자 / 공백 허용 (최대 {max}자)',
    en: 'Korean / English / digits / spaces allowed (max {max} chars)',
    ja: '韓国語 / 英語 / 数字 / 空白可 (最大{max}文字)',
  },

  // ----- Step2 — 도메인 등록 -----
  // 본문에 <code> 가 있어 분리 (JSX 결합):
  //   {before_code} <code>name@<도메인></code> {after_code}
  'onboarding.step2.subheading_before_code': {
    ko: '회사 도메인을 입력하세요. 사용자 계정은',
    en: 'Enter your company domain. User accounts will be in the form',
    ja: '会社のドメインを入力してください。ユーザーアカウントは',
  },
  'onboarding.step2.subheading_after_code': {
    ko: '형태로 만들어집니다.',
    en: '.',
    ja: 'の形式で作成されます。',
  },
  'onboarding.step2.label_domain': { ko: '도메인', en: 'Domain', ja: 'ドメイン' },

  // ----- Step3 — 도메인 검증 -----
  'onboarding.step3.subheading_after_code': {
    ko: '의 소유권을 확인합니다.',
    en: ' — verifying ownership.',
    ja: ' の所有権を確認します。',
  },
  'onboarding.step3.domain_unset': {
    ko: '(도메인 미설정)',
    en: '(domain unset)',
    ja: '(ドメイン未設定)',
  },
  'onboarding.step3.soft_drift_msg': {
    ko: '네트워크가 변경되어 재확인이 필요합니다. 도메인을 다시 확인 후 "검증 시도"를 눌러 주세요.',
    en: 'Network changed — re-verification required. Recheck the domain, then press "Verify".',
    ja: 'ネットワークが変更されたため再確認が必要です。ドメインを確認後、「検証実行」を押してください。',
  },
  'onboarding.step3.resolver_count_suffix': {
    ko: 'resolver 발견',
    en: 'resolvers found',
    ja: 'のresolverを検出',
  },
  'onboarding.step3.remaining_attempts': {
    ko: '남은 시도: {n}회',
    en: 'Remaining attempts: {n}',
    ja: '残り試行回数: {n}回',
  },

  // DNS sub-flow
  'onboarding.step3.dns.label': {
    ko: '아래 TXT 레코드를 DNS 에 등록하세요',
    en: 'Register the following TXT record in DNS',
    ja: '以下のTXTレコードをDNSに登録してください',
  },
  'onboarding.step3.dns.challenge_unset': {
    ko: '(challenge value 미발급 — 새로고침 후 다시 시도)',
    en: '(challenge value unavailable — refresh and retry)',
    ja: '(challenge値未発行 — リロード後に再試行)',
  },
  'onboarding.step3.dns.aria_record': {
    ko: 'TXT record value',
    en: 'TXT record value',
    ja: 'TXTレコード値',
  },
  'onboarding.step3.dns.copy_aria': {
    ko: 'TXT 레코드 값 복사',
    en: 'Copy TXT record value',
    ja: 'TXTレコード値をコピー',
  },
  'onboarding.step3.dns.copied': { ko: '복사됨', en: 'Copied', ja: 'コピー済み' },
  'onboarding.step3.dns.copy': { ko: '복사', en: 'Copy', ja: 'コピー' },
  'onboarding.step3.dns.hint_host': { ko: '호스트', en: 'Host', ja: 'ホスト' },
  'onboarding.step3.dns.hint_type': { ko: '타입', en: 'Type', ja: 'タイプ' },
  'onboarding.step3.dns.hint_value': { ko: '값', en: 'Value', ja: '値' },
  'onboarding.step3.dns.hint_propagation': {
    ko: '위의 문자열 전체. DNS 전파에 최대 30분이 걸릴 수 있습니다.',
    en: 'the full string above. DNS propagation can take up to 30 minutes.',
    ja: '上記文字列全体。DNS伝播に最大30分かかる場合があります。',
  },
  'onboarding.step3.dns.btn_verify': {
    ko: '검증 시도',
    en: 'Verify',
    ja: '検証実行',
  },

  // MX OTP sub-flow
  'onboarding.step3.mx.hint_send_after_code': {
    ko: '으로 6자리 인증 코드를 발송합니다.',
    en: ' — sending a 6-digit verification code.',
    ja: ' に6桁の認証コードを送信します。',
  },
  'onboarding.step3.mx.btn_send': { ko: 'OTP 발송', en: 'Send OTP', ja: 'OTP送信' },
  'onboarding.step3.mx.label_otp': {
    ko: '인증 코드 (6자리)',
    en: 'Verification code (6 digits)',
    ja: '認証コード (6桁)',
  },
  'onboarding.step3.mx.dev_bypass_prefix': {
    ko: '[dev bypass] OTP',
    en: '[dev bypass] OTP',
    ja: '[dev bypass] OTP',
  },
  'onboarding.step3.mx.btn_resend': { ko: '다시 발송', en: 'Resend', ja: '再送' },
  'onboarding.step3.mx.btn_confirm': { ko: '확인', en: 'Confirm', ja: '確認' },

  // ----- Step4 — 관리자 계정 -----
  'onboarding.step4.heading': {
    ko: '관리자 계정 생성',
    en: 'Create admin account',
    ja: '管理者アカウント作成',
  },
  'onboarding.step4.subheading_before_strong': {
    ko: '이 계정은 회사의',
    en: 'This account holds',
    ja: 'このアカウントは会社の',
  },
  'onboarding.step4.subheading_role': {
    ko: '최고 관리자(super-admin)',
    en: 'super-admin',
    ja: 'スーパー管理者 (super-admin)',
  },
  'onboarding.step4.subheading_after_strong': {
    ko: '권한을 가집니다.',
    en: 'privileges for the company.',
    ja: '権限を持ちます。',
  },
  'onboarding.step4.label_email': { ko: '관리자 이메일', en: 'Admin email', ja: '管理者メール' },
  'onboarding.step4.placeholder_local': { ko: 'admin', en: 'admin', ja: 'admin' },
  'onboarding.step4.err_local': {
    ko: '영문/숫자/. _ - 조합, 최대 64자. (예: admin)',
    en: 'Letters/digits/. _ - allowed, max 64 chars. (e.g. admin)',
    ja: '英数字/. _ - 組み合わせ、最大64文字 (例: admin)',
  },
  'onboarding.step4.label_pw': { ko: '비밀번호', en: 'Password', ja: 'パスワード' },
  'onboarding.step4.aria_pw_hide': {
    ko: '비밀번호 숨기기',
    en: 'Hide password',
    ja: 'パスワードを隠す',
  },
  'onboarding.step4.aria_pw_show': {
    ko: '비밀번호 표시',
    en: 'Show password',
    ja: 'パスワードを表示',
  },
  'onboarding.step4.btn_hide': { ko: '숨김', en: 'Hide', ja: '隠す' },
  'onboarding.step4.btn_show': { ko: '표시', en: 'Show', ja: '表示' },
  'onboarding.step4.pw_strength.0': { ko: '매우 약함', en: 'Very weak', ja: '非常に弱い' },
  'onboarding.step4.pw_strength.1': { ko: '약함', en: 'Weak', ja: '弱い' },
  'onboarding.step4.pw_strength.2': { ko: '보통', en: 'Fair', ja: '普通' },
  'onboarding.step4.pw_strength.3': { ko: '강함', en: 'Strong', ja: '強い' },
  'onboarding.step4.pw_strength.4': { ko: '매우 강함', en: 'Very strong', ja: '非常に強い' },
  'onboarding.step4.pw_strength_prefix': { ko: '강도', en: 'Strength', ja: '強度' },
  'onboarding.step4.pw_hint_after_strength': {
    ko: '· 최소 10자 / 영문 대소·숫자·기호 조합 권장',
    en: '· min 10 chars / mix of upper, lower, digits, symbols recommended',
    ja: '· 最低10文字 / 英大小・数字・記号の混在を推奨',
  },
  'onboarding.step4.label_name': {
    ko: '이름 (표시용, 선택)',
    en: 'Name (display, optional)',
    ja: '名前 (表示用、任意)',
  },
  'onboarding.step4.placeholder_name': { ko: '홍길동', en: 'John Doe', ja: '山田太郎' },
  'onboarding.step4.btn_submit': { ko: '가입 완료', en: 'Complete sign-up', ja: '登録完了' },

  // ----- 공통 버튼 (여러 step 공유) -----
  'onboarding.btn.prev': { ko: '이전', en: 'Back', ja: '戻る' },
  'onboarding.btn.next': { ko: '다음 단계', en: 'Next', ja: '次へ' },
  'onboarding.btn.processing': { ko: '진행 중...', en: 'Processing…', ja: '処理中...' },
  'onboarding.btn.registering': { ko: '등록 중...', en: 'Registering…', ja: '登録中...' },
  'onboarding.btn.verifying': { ko: '확인 중...', en: 'Verifying…', ja: '確認中...' },
  'onboarding.btn.sending': { ko: '발송 중...', en: 'Sending…', ja: '送信中...' },
  'onboarding.btn.submitting': { ko: '가입 중...', en: 'Submitting…', ja: '登録中...' },
  'onboarding.btn.to_login': { ko: '로그인 화면으로', en: 'Go to login', ja: 'ログイン画面へ' },

  // ===== Cycle 56 — shared 3 + expired/race-rejected =====

  // ----- DomainAvailabilityIndicator (shared) -----
  'onboarding.indicator.idle': {
    ko: '도메인을 입력해 주세요 (예: timesoft.co.kr)',
    en: 'Enter a domain (e.g. timesoft.co.kr)',
    ja: 'ドメインを入力してください (例: timesoft.co.kr)',
  },
  'onboarding.indicator.checking': { ko: '확인 중...', en: 'Checking…', ja: '確認中...' },
  'onboarding.indicator.available': {
    ko: '사용 가능한 도메인입니다.',
    en: 'Domain is available.',
    ja: '使用可能なドメインです。',
  },
  'onboarding.indicator.duplicate': {
    ko: '이미 등록된 도메인입니다.',
    en: 'Domain is already registered.',
    ja: '既に登録されているドメインです。',
  },
  'onboarding.indicator.public_mail': {
    ko: '개인 이메일 도메인은 사용할 수 없습니다.',
    en: 'Personal email domains are not allowed.',
    ja: '個人メールドメインは使用できません。',
  },
  'onboarding.indicator.invalid': {
    ko: '도메인 형식이 올바르지 않습니다.',
    en: 'Invalid domain format.',
    ja: 'ドメインの形式が正しくありません。',
  },
  'onboarding.indicator.error': {
    ko: '확인할 수 없습니다.',
    en: 'Unable to verify.',
    ja: '確認できません。',
  },

  // ----- RestoreBanner (shared) -----
  'onboarding.restore.fallback_company': {
    ko: '진행 중인 가입',
    en: 'Sign-up in progress',
    ja: '進行中の登録',
  },
  'onboarding.restore.message_with_step': {
    ko: '— Step {step} 부터 이어서 진행할 수 있습니다.',
    en: '— resume from Step {step}.',
    ja: '— Step {step} から再開できます。',
  },
  'onboarding.restore.btn_restart': {
    ko: '처음부터 시작하기',
    en: 'Start over',
    ja: '最初からやり直す',
  },

  // ----- VerificationMethodSelector (shared) -----
  'onboarding.method.aria_radiogroup': {
    ko: '검증 방법',
    en: 'Verification method',
    ja: '検証方法',
  },
  'onboarding.method.mx_disabled_title': {
    ko: '이메일 인증은 다음 업데이트에서 활성화됩니다.',
    en: 'Email verification will be enabled in a future update.',
    ja: 'メール認証は今後のアップデートで有効化されます。',
  },
  'onboarding.method.dns': { ko: 'DNS TXT 레코드', en: 'DNS TXT record', ja: 'DNS TXTレコード' },
  'onboarding.method.mx_coming_soon': {
    ko: '이메일 인증 (준비 중)',
    en: 'Email verification (coming soon)',
    ja: 'メール認証 (準備中)',
  },

  // ----- OnboardingExpiredPage -----
  'onboarding.expired.heading': {
    ko: '세션이 만료되었습니다',
    en: 'Session expired',
    ja: 'セッションが期限切れです',
  },
  'onboarding.expired.line1': {
    ko: '회사 가입 진행 정보가 24시간이 지나 만료되었습니다.',
    en: 'Your sign-up session expired after 24 hours.',
    ja: '会社登録の進行情報が24時間経過し期限切れになりました。',
  },
  'onboarding.expired.line2': {
    ko: '처음부터 다시 시작해 주세요.',
    en: 'Please start over.',
    ja: '最初からやり直してください。',
  },
  'onboarding.expired.btn_restart': {
    ko: '다시 시작',
    en: 'Restart',
    ja: 'やり直す',
  },

  // ----- OnboardingRaceRejectedPage -----
  'onboarding.race.heading': {
    ko: '동시에 다른 사용자가 가입을 진행 중입니다',
    en: 'Another user is signing up concurrently',
    ja: '他のユーザーが同時に登録中です',
  },
  'onboarding.race.line1': {
    ko: '같은 도메인으로 다른 사용자가 가입을 진행 중이라 더 진행할 수 없습니다.',
    en: 'Another user is signing up with the same domain — you cannot proceed.',
    ja: '同じドメインで他のユーザーが登録中のため続行できません。',
  },
  'onboarding.race.line2': {
    ko: '다른 도메인을 사용하거나 운영팀에 문의해 주세요.',
    en: 'Use a different domain or contact the operations team.',
    ja: '別のドメインを使用するか、運営チームにお問い合わせください。',
  },
  'onboarding.race.btn_retry_diff_domain': {
    ko: '다른 도메인으로 다시 시도',
    en: 'Retry with another domain',
    ja: '別のドメインで再試行',
  },

  // ----- Cycle 72 — OnboardingPendingApprovalPage (셀프가입 후 승인 대기) -----
  'onboarding.pending_approval.heading': {
    ko: '가입 신청이 접수되었습니다',
    en: 'Your sign-up has been submitted',
    ja: '登録申請を受け付けました',
  },
  'onboarding.pending_approval.line1': {
    ko: '회사 가입 신청이 운영팀 승인 대기 중입니다.',
    en: 'Your company sign-up is awaiting approval by the operations team.',
    ja: '企業登録申請は運営チームの承認待ちです。',
  },
  'onboarding.pending_approval.line2': {
    ko: '승인이 완료되면 입력하신 이메일로 안내가 발송되며, 그 후 로그인할 수 있습니다.',
    en: 'Once approved, you will be notified at your email and can then log in.',
    ja: '承認後、入力されたメールに案内が送信され、その後ログインできます。',
  },
};

export type OnboardingI18nKey = keyof typeof MESSAGES;

export function tOnb(key: OnboardingI18nKey, params?: Record<string, string | number>): string {
  const m = MESSAGES[key];
  if (!m) return key;
  const raw = m[getSharedLang()] ?? m.en ?? m[FALLBACK_LANG] ?? key;
  if (!params) return raw;
  // 단순 {name} 치환 — recursion 없음
  return raw.replace(/\{(\w+)\}/g, (_, name) => {
    const v = params[name];
    return v === undefined || v === null ? `{${name}}` : String(v);
  });
}

export function getOnboardingLang(): Lang { return getSharedLang(); }
export function setOnboardingLang(value: Lang): void { setSharedLang(value); }
export function subscribeOnboardingLang(fn: () => void): () => void {
  return subscribeSharedLang(fn);
}
